import React from 'react';
import { Box, useMediaQuery, useTheme, Typography } from '@mui/material';

// Assets
import BackgroundImage from '../../assets/centrum_assets/design/Benefits/BACKGROUND.png';
import TopBorderImage from '../../assets/centrum_assets/design/Benefits/TOP_BORDER_DESIGN.png';
import BottomBorderImage from '../../assets/centrum_assets/design/Benefits/BOTTOM_ORDER_DESIGN_BG.png';
import iPhoneMockup from '../../assets/centrum_assets/design/Benefits/IPHON_ MOCK_UP.png';
import qrcode from '../../assets/centrum_assets/design/Benefits/qrcode.png';
import AppStoreImg from '../../assets/centrum_assets/design/Benefits/APPLE_STORE.png';
import GooglePlayImg from '../../assets/centrum_assets/design/Benefits/GOOGLE_PLAY.png';

// Components
import FAQSection from '../pages/benefitssectioning/FAQSection';
import LinesDecor from '../components/LinesDecor';

const CONTENT_Z_INDEX = 10;

// --- Sub-Components ---

const DownloadButtons = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xl: 10, md: 'auto' } }}>
    {/* Google Play Button */}
    <Box
      component="a"
      href="https://play.google.com/store/apps/details?id=com.centrumfuel.benefits.official&pcampaignid=web_share"
      target="_blank"
      rel="noopener noreferrer"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 'auto', height: 50, borderRadius: 2, p: 1 }}
    >
      <Box component="img" src={GooglePlayImg} alt="Google Play" sx={{ width: '80%', height: 'auto' }} />
    </Box>

    {/* Apple App Store Button */}
    <Box
      component="a"
      href="https://apps.apple.com/ph/app/centrum-fuel-benefits/id6760686385"
      target="_blank"
      rel="noopener noreferrer"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 'auto', height: 50, borderRadius: 2, p: 1 }}
    >
      <Box component="img" src={AppStoreImg} alt="App Store" sx={{ width: '80%', height: 'auto' }} />
    </Box>
  </Box>
);

const QRCodeBlock = () => (
  <Box
    sx={{
      width: { xs: 110, sm: 180, md: 180 },
      minWidth: { xs: 110, sm: 180, md: 180 },
      backgroundColor: '#000',
      borderRadius: 2,
      p: 1,
      textAlign: 'center',
      mr: 2,
    }}
  >
    <Box component="img" src={qrcode} sx={{ width: '100%', borderRadius: 1 }} />
    <Typography sx={{ color: '#fff', fontSize: '1rem', mt: 1 }}>
      SCAN QR CODE OR CLICK TO
    </Typography>
    <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.5rem' }}>
      DOWNLOAD
    </Typography>
  </Box>
);

const InfoContent = ({ isMobileView = false }) => (
  <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'flex-start', flexWrap: 'nowrap' }}>
    <QRCodeBlock width={isMobileView ? 110 : 180} />
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', maxWidth: isMobileView ? 'none' : 600 }}>
      <Typography
        sx={{
          fontWeight: 700,
          fontStyle: 'italic',
          color: '#0b1b3f',
          mb: 0.5,
          fontSize: isMobileView ? { xs: '1.8rem', sm: '2rem' } : { lg: 25, md: 24, sm: 22, xs: 20 },
          '@media (min-width:768px)': isMobileView ? { fontSize: '3rem' } : {},
        }}
      >
        FUEL UP, SAVE MORE, DRIVE SMART
      </Typography>
      <Typography
        sx={{
          color: '#333',
          fontWeight: 600,
          mb: 2,
          lineHeight: isMobileView ? 1.4 : 1.6,
          fontSize: isMobileView ? 12.5 : { lg: 16, md: 13 },
          whiteSpace: isMobileView ? 'normal' : 'pre-line',
          '@media (min-width:450px)': isMobileView ? { fontSize: '2.2rem' } : {},
        }}
      >
        {isMobileView
          ? "Every time you fuel up, you earn points to get discounts next time. Sign up once, scan your code, save more every trip."
          : `Every time you fuel up, you earn points to get discounts next time.\nSign up once, scan your code, save more every trip.\nNo hassle. No extra cost.`}
      </Typography>
      <DownloadButtons />
    </Box>
  </Box>
);

// --- MAIN COMPONENT ---

const Benefits = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const headerFontSize = { xs: 40, sm: 60, md: 50, lg: 70, xl: 90 };
  const subHeaderFontSize = { xs: 30, sm: 40, md: 35, lg: 45, xl: 60 };

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          backgroundImage: `url(${BackgroundImage})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          overflow: 'hidden',
          minHeight: 'auto',
          '@media (max-width:344px)': { minHeight: '90vh' },
          '@media (min-width:912px) and (max-width:1024px)': { minHeight: '75vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Top Border */}
        <Box
          component="img"
          src={TopBorderImage}
          alt="Top Border"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            objectFit: 'cover',
            transformOrigin: 'top center',
            zIndex: 1,
            transform: {
              xs: 'translateY(-60%) scale(1.4)',
              sm: 'translateY(-50%) scale(1.2)',
              md: 'translateY(-50%) scale(1.1)',
              lg: 'translateY(-45%) scale(1.1)',
            },
          }}
        />

        {/* Floating Header */}
        <Box sx={{ position: 'absolute', top: isMobile ? 20 : 40, width: '100%', textAlign: 'center', zIndex: 30 }}>
          <Typography
            sx={{
              px: 2,
              whiteSpace: 'nowrap',
              fontWeight: 600,
              fontStyle: 'italic',
              letterSpacing: 2,
              color: '#888686ff',
              fontSize: { lg: '3.75rem', md: '3rem', sm: '3rem', xs: '2.125rem' },
            }}
          >
            THE ALL NEW
          </Typography>
        </Box>

        {/* Main Content Layout */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: { xs: 'flex-start', md: 'space-between' },
            alignItems: 'center',
            px: { xs: 1, sm: 4, md: 5, lg: 10 },
            pt: { xs: 18, sm: 20, md: 15 },
            position: 'relative',
            zIndex: CONTENT_Z_INDEX,
          }}
        >
          {/* Left Side Content */}
          <Box sx={{ width: { xs: '100%', md: '50%' }, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', order: 1, zIndex: 30 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { lg: 'flex-end', md: 'flex-end', xs: 'center' }, width: '100%', mx: 5 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontStyle: 'italic',
                  color: '#0b1b3f',
                  whiteSpace: 'nowrap',
                  fontSize: headerFontSize,
                  lineHeight: 1,
                  
                  mt: { xl: -25, lg: -15, md: -5, sm: -1, xs: -7 },
                  '@media (max-width:344px)': { mt: -28 },
                }}
              >
                CENTRUM FUEL
              </Typography>
              <Typography sx={{ fontSize: subHeaderFontSize, ml: { xs: 25, sm: 37, md: 35, lg: 35, xl: 44 }, mx: 5, fontWeight: 500, fontStyle: 'italic', color: '#0b1b3f' }}>
                BENEFITS
              </Typography>
            </Box>

            {/* Desktop Only Info Block */}
            <Box sx={{ display: { xs: 'none', md: 'block' }, pt: 4 }}>
              <InfoContent isMobileView={false} />
            </Box>
          </Box>

          {/* iPhone Mockup Container */}
          <Box
            sx={{
              width: { xs: '90%', sm: '90%', md: '45%', lg: '45%', xl: '120%' },
              maxWidth: 800,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-end',
              order: 2,
              position: 'relative',
              mt: { xs: -1, sm: 0, md: -10 },
              zIndex: 5,
              '@media (max-width:344px)': { mt: -15 },
            }}
          >
            <Box component="img" src={iPhoneMockup} alt="iPhone Mockup" sx={{ width: '100%', height: 'auto', objectFit: 'contain' }} />
          </Box>
        </Box>

        {/* Bottom Border */}
        <Box
          component="img"
          src={BottomBorderImage}
          alt="Bottom Border"
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '110%',
            objectFit: 'cover',
            zIndex: { xs: 20, sm: 20, md: 5, lg: 5, xl: 5 },
            transformOrigin: 'bottom center',
            pointerEvents: 'none',
            transform: {
              xs: 'translate(50%, 90%) scale(3.2)',
              sm: 'translate(30%, 60%) scale(2.3)',
              md: 'translate(0%, 80%) scale(1.5)',
            },
            '@media (max-width:360px)': { transform: 'translate(65%, 100%) scale(3.7)' },
            '@media (width:820px)': { transform: 'translate(65%, 150%) scale(3.7)' },
            '@media (width:768px)': { transform: 'translate(65%, 150%) scale(3.7)' },
          }}
        />
      </Box>

      {/* Mobile Only */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, position: 'relative' }}>
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            width: '100%',
            zIndex: 40,
            p: 2,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <InfoContent isMobileView={true} />
        </Box>
      </Box>

      {/* Decor & FAQ */}
      <Box sx={{ position: 'relative', width: '100%' }}>
        <LinesDecor
          greenMt={{ xl: -5, lg: -5, md: -4, sm: -2, xs: 1 }}
          blueMt={{ xl: 0, lg: 1, md: 2, sm: 1, xs: 3 }}
        />
      </Box>
      <FAQSection />
    </>
  );
};

export default Benefits;